import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FaArrowLeft } from 'react-icons/fa';
import { axiosInstance } from '../axiosInstance';

const PaymentCallbackPage = () => {
  const [status, setStatus] = useState('verifying'); // verifying, success, failed, error
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyPayment();
    // eslint-disable-next-line
  }, []);

  const verifyPayment = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference');
    const trxref = urlParams.get('trxref');
    if (!reference && !trxref) {
      setStatus('error');
      setError('No payment reference found');
      return;
    }
    const paymentRef = reference || trxref;
    try {
      const response = await axiosInstance.get(`/api/invoice/payment/verify?reference=${paymentRef}`);
      const data = response.data;
      if (data.error === false && data.data) {
        setStatus('success');
        setPaymentData(data.data);
      } else {
        setStatus('failed');
        setError(data.message || 'Payment verification failed');
        setPaymentData(data.data || null);
      }
    } catch (err) {
      setStatus('error');
      setError('Unable to verify payment. Please contact support.');
      setPaymentData(null);
      console.error('Verification error:', err);
    }
  };

  // Verifying State
  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <CircularProgress color="primary" style={{ width: 64, height: 64, marginBottom: 16 }} />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
          <div className="mt-6">
            <div className="flex justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Card */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-center">
              <CheckCircleIcon className="w-20 h-20 text-white mx-auto mb-4" style={{ fontSize: 80, margin: 'auto', marginBottom: 16 }} />
              <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-green-100">Your payment has been processed successfully</p>
            </div>
            {/* Payment Details */}
            <div className="p-8">
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">Invoice Number</span>
                  <span className="text-lg font-bold text-gray-900">#{paymentData?.invoice_no}</span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">Order Number</span>
                  <span className="text-lg font-semibold text-gray-900">{paymentData?.invoice_no}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Amount Paid</span>
                  <span className="text-2xl font-bold text-green-600">
                    ₦{Number(paymentData?.amount).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              {/* Transaction Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Transaction Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className="font-semibold text-green-600 uppercase">{paymentData?.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method</span>
                    <span className="font-medium text-gray-900">Paystack</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction Date</span>
                    <span className="font-medium text-gray-900">
                      {paymentData?.transaction_date
                        ? new Date(paymentData.transaction_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : new Date().toLocaleDateString('en-US',{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                    </span>
                  </div>
                </div>
              </div>
              {/* Action Buttons */}
              {/* <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleDownloadReceipt}
                  className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  <DownloadIcon className="w-5 h-5 mr-2" />
                  Download Receipt
                </button>
                <button
                  onClick={handleSendReceipt}
                  disabled={sendingReceipt}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  {sendingReceipt ? (
                    <>
                      <CircularProgress size={20} color="inherit" className="mr-2" style={{ marginRight: 8 }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <EmailIcon className="w-5 h-5 mr-2" />
                      Email Receipt
                    </>
                  )}
                </button>
              </div> */}
              {/* Support Info */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  Need help? Contact our support team at{' '}
                  <a href="mailto:support@01pos.net" className="text-blue-600 hover:underline font-medium">
                    support@01pos.net
                  </a>
                </p>
              </div>
            </div>
          </div>
          {/* Back to Dashboard */}
          <div className="text-center">
            <a
              href="/dashboard"
              className="inline-block text-gray-600 hover:text-gray-900 font-medium transition-colors flex items-center justify-center gap-2"
              style={{ textDecoration: 'none', alignItems: 'center' }}
            >
              <FaArrowLeft style={{ marginRight: 6, fontSize: 16 }} /> Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Failed State
  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <CancelIcon className="w-20 h-20 text-red-500 mx-auto mb-4" style={{ fontSize: 80, color: "#ef4444", margin: 'auto', marginBottom: 16 }} />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6">
              {error || 'Unfortunately, your payment could not be processed.'}
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-semibold text-red-900 mb-2">Common reasons:</h3>
              <ul className="text-sm text-red-800 space-y-1">
                <li>• Insufficient funds in your account</li>
                <li>• Card declined by your bank</li>
                <li>• Incorrect card details</li>
                <li>• Network connection issues</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() =>
                  window.location.href = `/invoice/payment/${paymentData?.invoice_id || ''}`
                }
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Try Again
              </button>
              <a
                href="/support"
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-colors text-center"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <ErrorOutlineIcon className="w-16 h-16 text-orange-500 mx-auto mb-4" style={{ fontSize: 64, color: '#f59e42', margin: 'auto', marginBottom: 16 }} />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Error</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-orange-800">
            If you were charged, don't worry. Please contact our support team with your transaction reference and we'll help resolve this immediately.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={verifyPayment}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Retry Verification
          </button>
          <a
            href="mailto:support@01pos.net"
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
